import { Combobox } from '@ark-ui/react/combobox'
import { Portal } from '@ark-ui/react/portal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faXmark,
  faChevronDown,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import { cn } from '@/utils'
import type { FC } from 'react'
import { useMemo, useState } from 'react'
import { useUncontrolled } from '@mantine/hooks'
import { createListCollection } from '@ark-ui/react/collection'

type MultiSelectItem = {
  value: string
  label: string
}

type MultiSelectProps = {
  value: string[]
  onChange: (value: string[]) => void
  options: MultiSelectItem[]
  placeholder?: string
  loading?: boolean
}

export const MultiSelect: FC<MultiSelectProps> = ({
  value,
  onChange,
  placeholder = '',
  options,
  loading,
}) => {
  const [selectedValues, setSelectedValues] = useUncontrolled({
    value,
    onChange,
    defaultValue: [],
  })

  const [searchQuery, setSearchQuery] = useState('')

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options
    const query = searchQuery.toLowerCase()
    return options.filter((opt) => opt.label.toLowerCase().includes(query))
  }, [options, searchQuery])

  // Create collection from filtered options
  const collection = useMemo(
    () =>
      createListCollection<MultiSelectItem>({
        items: filteredOptions,
        itemToString: ({ label }) => label,
        itemToValue: (item) => item?.value,
      }),
    [filteredOptions],
  )

  // Get selected items from all options (not just filtered)
  const selectedItems = useMemo(() => {
    const valueToItem = new Map(options.map((opt) => [opt.value, opt]))
    return selectedValues
      .map((v) => valueToItem.get(v))
      .filter(Boolean) as MultiSelectItem[]
  }, [selectedValues, options])

  const handleInputChange = (details: Combobox.InputValueChangeDetails) => {
    setSearchQuery(details.inputValue)
  }

  const handleValueChange = (
    details: Combobox.ValueChangeDetails<MultiSelectItem>,
  ) => {
    const newValues = details.items.map((item) => item.value)
    setSelectedValues(newValues)
  }

  const removeValue = (index: number) => {
    const newValues = selectedValues.filter((_, i) => i !== index)
    setSelectedValues(newValues)
  }

  return (
    <div className="w-full max-w-md">
      <Combobox.Root
        collection={collection}
        onInputValueChange={handleInputChange}
        onValueChange={handleValueChange}
        value={selectedItems.map(({ value }) => value)}
        multiple
        openOnClick
        immediate
        disabled={loading}
      >
        <Combobox.Control
          className={cn(
            'relative rounded-md',
            'bg-gray-800',
            'transition-all ease-in-out duration-300',
            'focus-within:ring-2 focus-within:ring-blue-500',
            'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
          )}
        >
          <div className="flex items-center flex-wrap gap-1.5 p-1.5 pr-7">
            {selectedItems.map(({ value, label }, index) => (
              <div
                key={`${value}-${index}`}
                className={cn(
                  'inline-flex items-center gap-0.5',
                  'px-2 py-0.5 rounded',
                  'bg-gray-600 text-white text-sm',
                  'transition-colors',
                )}
              >
                <span className="text-xs">{label}</span>
                <div className="shrink-0">
                  <button
                    type="button"
                    onClick={() => removeValue(index)}
                    className="hover:bg-gray-700 rounded px-1 transition-colors"
                  >
                    <FontAwesomeIcon icon={faXmark} size="xs" />
                  </button>
                </div>
              </div>
            ))}
            <Combobox.Input
              placeholder={selectedValues.length === 0 ? placeholder : ''}
              className={cn(
                'flex-1 bg-transparent w-full text-ellipsis',
                'focus:!ring-0 outline-none px-1 py-1 rounded-sm',
                'text-white placeholder:text-gray-400',
              )}
            />
          </div>
          <Combobox.Trigger
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2',
              'text-gray-400 hover:text-white',
              'transition-all will-change-transform ease-in-out duration-300',
              'data-[state=open]:rotate-180',
              loading && 'animate-spin',
            )}
          >
            <FontAwesomeIcon
              icon={loading ? faSpinner : faChevronDown}
              size="xs"
            />
          </Combobox.Trigger>
        </Combobox.Control>
        <Portal>
          <Combobox.Positioner>
            <Combobox.Content
              className={cn(
                'bg-gray-700 rounded-md shadow-lg',
                'border border-gray-600',
                'py-1 mt-1 overflow-hidden',
                'max-h-[300px] overflow-y-auto',
                'z-50',
                loading && 'pointer-events-none',
              )}
            >
              <Combobox.ItemGroup>
                {collection.items.map((item) => (
                  <Combobox.Item
                    key={item.value}
                    item={item}
                    className={cn(
                      'flex gap-2 items-center',
                      'py-2 px-3 cursor-pointer',
                      'transition-colors',
                      'text-white',
                      loading ? 'pointer-events-none' : 'hover:bg-gray-600',
                    )}
                  >
                    <Combobox.ItemIndicator className="shrink-0">
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-500"
                      />
                    </Combobox.ItemIndicator>
                    <Combobox.ItemText>{item.label}</Combobox.ItemText>
                  </Combobox.Item>
                ))}
              </Combobox.ItemGroup>
              <Combobox.Empty className="p-2 text-gray-300 text-sm text-center">
                No results found.
              </Combobox.Empty>
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </Combobox.Root>
    </div>
  )
}
